import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  Output,
  EventEmitter,
  SimpleChanges,
} from '@angular/core';
import { UserType } from '@shared-features/enums/user-type.enum';
import { Shipment } from '@shared-features/models/shipment';
import { ShipmentAction } from '@shared-features/enums/shipment-action.enum';
import { getDurationInDetails } from '@shared/utils/duration.util';
import { MatDialog } from '@angular/material/dialog';
import { EditGateToGateDialogComponent } from '@shared-features/components/edit-gate-to-gate-dialog/edit-gate-to-gate-dialog.component';
import { take } from 'rxjs/operators';
import { ShipmentHistoryUpdate } from '@features/shipments/models/shipment-history-update.model';
import { RolesConstants } from '@shared/model/roles-constants';
import { ShipmentsCommonApiService } from '@shared-features/services/shipments-common-api.service';
import { BaseComponent } from '@shared/components/base-component/base.component';
import { DatePipe } from '@angular/common';
import { NoopScrollStrategy } from '@angular/cdk/overlay';
import { environment } from '@env/environment';
import { IconType } from '@shared/enums/icon-type.enum';
import { ShipmentHeadedStatus } from '@shared-features/enums/shipment-headed-status';
import { SectionStateStatus } from '@shared/enums';

@Component({
  selector: 'app-new-gate-to-gate',
  templateUrl: './new-gate-to-gate.component.html',
  styleUrls: ['./new-gate-to-gate.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NewGateToGateComponent extends BaseComponent {
  @Input() shipment: Shipment = new Shipment();
  @Output() reload = new EventEmitter<void>();

  ShipmentAction = ShipmentAction;
  loadingOffTime: string;
  droppingOffTime: string;
  etaTime: any;
  itemsHistoryMap: { [id: number]: ShipmentHistoryUpdate[] } = {};
  itemsHistoryTextMap: { [id: number]: string } = {};
  itemsHistoryCountMap: { [id: number]: number } = {};
  formatedShipment;
  shipmentLogsUrl: string;
  enabledEmailFields: Map<string, boolean> = new Map<string, boolean>();
  attachmentEmailFields = new Map<string, boolean>();
  ActionPerConfig: { [key: string]: string } = {};
  protected IconType = IconType;
  protected readonly ShipmentHeadedStatus = ShipmentHeadedStatus;
  protected readonly SectionStateStatus = SectionStateStatus;

  private datePipe = new DatePipe(this.translationService.currentLang);
  readonly RolesConstants = RolesConstants;
  constructor(
    private dialogService: MatDialog,
    private shipmentsCommonApiService: ShipmentsCommonApiService,
    private changeDetectorRef: ChangeDetectorRef
  ) {
    super();
  }

  public get UserType(): typeof UserType {
    return UserType;
  }

  ngOnInit() {
    this.extractEmailConfigData();
    this.reformatShipmentHistory();
    if (environment.shipmentLogsUrl) {
      this.shipmentLogsUrl = environment.shipmentLogsUrl;
    }
  }

  reformatShipmentHistory() {
    // check if shipment has order history and reformat the object to disply order history
    const orderMap = new Map();
    const shipmentHistory = [];
    for (const [index, history] of this.shipment.history.entries()) {
      if (history.order) {
        if (orderMap.has(history.order.id)) {
          orderMap.get(history.order.id).history.push(history);
        } else {
          orderMap.set(history.order.id, { index, history: [history], order: history.order });
        }
      } else {
        shipmentHistory.push(history);
      }
    }

    orderMap.forEach((v, k) => {
      shipmentHistory.splice(v.index, 0, {
        order: v.order,
        history: v.history,
      });
    });
    this.formatedShipment = shipmentHistory.map((shipment) => {
      const word = this.ActionPerConfig[shipment.action] + 'AttachmentEmailEnabled';

      return {
        ...shipment,
        isEmailSent: this.enabledEmailFields.get(
          this.ActionPerConfig[shipment.action] + 'EmailEnabled'
        ),
        isAttachmentSent: ['DriverIqama', 'DriverLicense', 'TruckLicense']
          .map((state) => {
            return this.attachmentEmailFields.get(
              this.ActionPerConfig[shipment.action] + state + 'AttachmentEmailEnabled'
            );
          })
          .find((element) => element),
      };

      this.changeDetectorRef.markForCheck();
    });
  }

  timeConvert(n: number) {
    const hours = Math.floor(n);
    const min = Math.round((n - hours) * 60);
    return +hours + ' hour(s) and ' + min + ' minute(s).';
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.shipment && changes.shipment.currentValue) {
      const createdTime = this.shipment.history[0]?.createdAt || 0;
      const deliveredTime = this.shipment.history[this.shipment.history.length - 1].createdAt || 0;
      this.loadingOffTime = this.timeConvert(this.shipment?.loadingOffTime) || '0';
      this.droppingOffTime = this.timeConvert(this.shipment?.droppingOffTime) || '0';
      this.etaTime = getDurationInDetails(deliveredTime - createdTime);

      const group = this.shipment.history.reduce((g, item) => {
        g[item.id] = item.modifyHistory || [];
        return g;
      }, {});
      const countGroup = this.shipment.history.reduce((g, item) => {
        g[item.id] = item.modifiedCount || 0;
        return g;
      }, {});
      this.itemsHistoryMap = Object.assign({}, this.itemsHistoryMap, group);
      this.itemsHistoryCountMap = Object.assign({}, this.itemsHistoryCountMap, countGroup);
    }
    this.reformatShipmentHistory();
    this.changeDetectorRef.markForCheck();
  }

  openEditTimeDialog(id: number, date: Date): void {
    const dialogRef = this.dialogService.open(EditGateToGateDialogComponent, {
      disableClose: true,
      scrollStrategy: new NoopScrollStrategy(),
      data: {
        id,
        date,
      },
    });

    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe((result) => {
        if (result) {
          const data = result.modifyHistory || [];
          this.itemsHistoryMap[result.id] = [];
          this.itemsHistoryCountMap[result.id] = data.length;
          this.changeDetectorRef.markForCheck();
          this.getItemUpdateHistory(result.id);
          this.reload.emit();
        }
      });
  }
  extractEmailConfigData() {
    const emailConfig = this.shipment.customer?.customerEmailConfig || {};
    const requiredEmailFields = [
      'isShipmentCreationEmailEnabled',
      'isShipmentAssignedEmailEnabled',
      'isShipmentArrivedPickupLocationEmailEnabled',
      'isShipmentLoadedEmailEnabled',
      'isShipmentArrivedDeliveryLocationEmailEnabled',
      'isShipmentDeliveredEmailEnabled',
      'isShipmentStartEmailEnabled',
    ];

    Object.entries(emailConfig).forEach(([key, value]) => {
      if (requiredEmailFields.includes(key) && value) {
        this.enabledEmailFields.set(key, !!value);
      } else if (key.endsWith('AttachmentEmailEnabled') && value) {
        this.attachmentEmailFields.set(key, !!value);
      }
    });
    this.ActionPerConfig = {
      SHIPMENT_CREATED: 'IsShipmentCreation',
      FLEET_ASSIGNED: 'IsShipmentAssigned',
      DRIVER_ASSIGNED: 'IsShipmentAssigned',
      TRIP_STARTED: 'IsShipmentStart',
      ARRIVED_AT_PICKUP_LOCATION: 'IsShipmentArrivedPickupLocation',
      GOODS_LOADED: 'IsShipmentLoaded',
      ARRIVED_AT_DROP_OFF_LOCATION: 'IsShipmentArrivedDeliveryLocation',
      GOODS_DELIVERED: 'IsShipmentDelivered',
    };
  }

  getItemUpdateHistory(id: number): void {
    this.load(this.shipmentsCommonApiService.getShipmentHistoryItem(id), {
      isLoadingTransparent: true,
    }).subscribe((res) => {
      this.itemsHistoryMap[id] = res.modifyHistory || [];
      this.itemsHistoryTextMap[id] = this.getHistoryUpdateText(res.modifyHistory);
      this.changeDetectorRef.markForCheck();
    });
  }

  getHistoryUpdateText(items: ShipmentHistoryUpdate[]): string {
    return `
      ${this.translate('sharedFeatures.GateToGate.DateModifyingHistory')}
      ${(items || [])
        .map((item) =>
          this.translate('sharedFeatures.GateToGate.DateUpdated', {
            name: item.updatedByName,
            date: this.datePipe.transform(item.previousDate, 'dd MMM yyyy HH:mm'),
          })
        )
        .join('\n')}
    `;
  }
}
